import React, { useState, useEffect, useCallback } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as DepartmentIcon,
} from '@mui/icons-material';
import {
  getDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  getDepartmentSettings,
  updateDepartmentSettings,
} from '../api';

export default function DepartmentSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [departments, setDepartments] = useState([]);
  const [departmentsEnabled, setDepartmentsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(3000);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDept, setDeletingDept] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, deptsRes] = await Promise.all([
        getDepartmentSettings(),
        getDepartments({ all: 'true' }),
      ]);
      setDepartmentsEnabled(!!settingsRes.data.departments_enabled);
      setDepartments(deptsRes.data);
    } catch (err) {
      setError('Failed to load department data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = async (e) => {
    const enabled = e.target.checked;
    setSaving(true);
    setError('');
    try {
      await updateDepartmentSettings({ departments_enabled: enabled });
      setDepartmentsEnabled(enabled);
      showSuccess(enabled ? 'Department tracking enabled' : 'Department tracking disabled');
    } catch (err) {
      setError('Failed to update setting');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingDept(null);
    setFormData({ name: '', description: '', is_active: true });
    setDialogOpen(true);
  };

  const handleOpenEdit = (dept) => {
    setEditingDept(dept);
    setFormData({ name: dept.name, description: dept.description || '', is_active: dept.is_active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Department name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingDept) {
        await updateDepartment(editingDept.id, formData);
        showSuccess('Department updated');
      } else {
        await createDepartment(formData);
        showSuccess('Department created');
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save department';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDept) return;
    setSaving(true);
    setError('');
    try {
      await deleteDepartment(deletingDept.id);
      setDeleteDialogOpen(false);
      setDeletingDept(null);
      showSuccess('Department deleted');
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete department';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DepartmentIcon /> Department Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      {/* Toggle Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={departmentsEnabled}
                onChange={handleToggle}
                disabled={saving}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Enable Department Tracking</Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, employees can be assigned a default department and time entries can be tagged with a department for cost reporting.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Department CRUD Table - only shown when enabled */}
      {departmentsEnabled && (
        <Paper sx={{ p: isMobile ? 1 : 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Departments</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} size={isMobile ? 'small' : 'medium'}>
              Add Department
            </Button>
          </Box>

          {departments.length === 0 ? (
            <Alert severity="info">No departments yet. Click "Add Department" to create one.</Alert>
          ) : isMobile ? (
            // Mobile card layout
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {departments.map((dept) => (
                <Card key={dept.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2">{dept.name}</Typography>
                        {dept.description && (
                          <Typography variant="body2" color="text.secondary">{dept.description}</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={dept.is_active ? 'Active' : 'Inactive'}
                          color={dept.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        <IconButton size="small" onClick={() => handleOpenEdit(dept)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => { setDeletingDept(dept); setDeleteDialogOpen(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop table layout
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell>{dept.name}</TableCell>
                      <TableCell>{dept.description || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={dept.is_active ? 'Active' : 'Inactive'}
                          color={dept.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(dept)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => { setDeletingDept(dept); setDeleteDialogOpen(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Department Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Department</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deletingDept?.name}"? Time entries using this department will have their department cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
